import { cloneElement, type ReactElement } from "react";
import { twJoin } from "tailwind-merge";

import { twVariants } from "@/helpers/tw-resolve";

type BadgeIcon = ReactElement<{ size?: number | string }>;

export interface BadgeBaseProps {
  size?: "xs" | "sm" | "md" | "lg";
  icon?: BadgeIcon;
  "data-testid"?: string;
  children?: React.ReactNode;
}

type BadgeProps = BadgeBaseProps & {
  variant?:
    | "primary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "pink"
    | "white"
    /** @deprecated legacy aliases — use error/primary/white */
    | "danger"
    | "special"
    | "default";
  /** Emphasised prefix rendered before the label, e.g. "Owner: Jane" (absorbs Pill). */
  prefix?: string;
  /** Adds a hover affordance for interactive chips (absorbs Tag). */
  clickable?: boolean;
};

export function Badge({
  variant = "white",
  size = "md",
  children,
  "data-testid": dataTestId,
  icon,
  prefix,
  clickable,
}: BadgeProps): React.ReactNode {
  const isIconOnly = icon && !children;
  const iconSize = {
    xs: 12,
    sm: 12,
    md: 14,
    lg: 16,
  }[size];

  const content = (
    <>
      {icon && cloneElement(icon, { size: iconSize })}
      {prefix && <span>{`${prefix}: `}</span>}
      {children && <span className="truncate">{children}</span>}
    </>
  );

  return (
    <span
      className={twJoin(badgeVariants({ variant, size, isIconOnly, clickable }), badgeTextColors[variant])}
      data-testid={dataTestId}
    >
      {content}
    </span>
  );
}

export const badgeVariants = twVariants({
  base: ["inline-flex size-fit max-w-full shrink items-center gap-xs overflow-hidden"],
  variants: {
    variant: {
      // DS V2 status/ colours
      primary: "bg-status-background-primary",
      error: "bg-status-background-error",
      info: "bg-status-background-info",
      success: "bg-status-background-success",
      warning: "bg-status-background-warning",
      pink: "bg-status-background-pink",
      white: "border border-border-default bg-status-background-white",
      // Legacy aliases → new tokens (removed once call sites migrate)
      default: "border border-border-default bg-status-background-white",
      danger: "bg-status-background-error",
      special: "bg-status-background-primary",
      custom: "",
    },
    size: {
      xs: "min-h-badge-height-xs rounded-label-xs text-label-xs",
      sm: "min-h-badge-height-sm rounded-md text-label-sm",
      md: "min-h-badge-height-md rounded-md text-label-md",
      lg: "min-h-badge-height-lg rounded-label-lg text-label-lg",
    },
    isIconOnly: {
      true: "",
      false: "",
    },
    clickable: {
      true: "cursor-pointer transition-colors hover:bg-background-secondary-hover",
      false: "",
    },
  },
  compoundVariants: [
    {
      size: ["xs"],
      isIconOnly: true,
      className: "p-badge-icon-xs",
    },
    {
      size: ["xs"],
      isIconOnly: false,
      className: "px-badge-inline-xs py-badge-block-xs",
    },
    {
      size: ["sm"],
      isIconOnly: true,
      className: "p-badge-icon-sm",
    },
    {
      size: ["sm"],
      isIconOnly: false,
      className: "px-badge-inline-sm py-badge-block-sm",
    },
    {
      size: ["md"],
      isIconOnly: true,
      className: "p-badge-icon-md",
    },
    {
      size: ["md"],
      isIconOnly: false,
      className: "px-badge-inline-md py-badge-block-md",
    },
    {
      size: ["lg"],
      isIconOnly: true,
      className: "p-badge-icon-lg",
    },
    {
      size: ["lg"],
      isIconOnly: false,
      className: "px-badge-inline-lg py-badge-block-lg",
    },
  ],
});

const badgeTextColors = {
  primary: "text-status-text-primary",
  error: "text-status-text-error",
  info: "text-status-text-info",
  success: "text-status-text-success",
  warning: "text-status-text-warning",
  pink: "text-status-text-pink",
  white: "text-status-text-white",
  default: "text-status-text-white",
  danger: "text-status-text-error",
  special: "text-status-text-primary",
} as const;
